/* eslint-disable @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */
/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */

import React, {
	Fragment,
	type ReactNode,
	type RefObject,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';

import {
	type Cell,
	type ColumnDef,
	flexRender,
	type Header,
	type Row as RowType,
	type TableOptions,
	type Row as TanRow,
	type TableState as TanTableState,
	useReactTable,
} from '@tanstack/react-table';

import Button, { IconButton } from '@atlaskit/button/new';
import { Checkbox } from '@atlaskit/checkbox';
import { Box, Flex, Inline, type XCSS, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { WidthObserver } from '@atlaskit/width-detector';

import {
	type TableState as CharlesState,
	type CharlieTableColumn,
	type CharlieTableHook,
	type CharlieTableRow,
} from '../hooks/use-charlie-table';
import { useInfiniteScroll } from '../shared-hooks/use-infinite-scroll';
import { useKeyboardNavigation } from '../shared-hooks/use-keyboard-navigation';
import { Col } from '../ui/primitives/col';
import { Colgroup } from '../ui/primitives/colgroup';
import { ExpandIcon } from '../ui/primitives/expand-icon';
import { TD } from '../ui/primitives/td';
import { TR } from '../ui/primitives/tr';

import { CharlieTableHeader } from './charlie-table-header';
import { TBody } from './components/body';
import { Row } from './components/row';
import { RowCell } from './components/row-cell';
import { Table } from './components/table';
import { THead } from './components/thead';
import { useConvertCharlieTableToTanStack } from './tanstack-converter';

/**
 * Type-safe enum for Table State
 */
export const TableState = {
	loaded: 'LOADED',
	loading: 'LOADING',
	error: 'ERROR',
} as const;
type TableState = (typeof TableState)[keyof typeof TableState];

/**
 * type defining the spacing for table body rows
 */
export type RowSpacing = 'compact' | 'default';

/**
 * Type-safe enum for Table Types. Naming from: https://hello.atlassian.net/wiki/spaces/CLOUDADMIN/pages/3888095386/Typology+of+Tables
 */
export const TableType = { inventory: 'INVENTORY', input: 'INPUT' } as const;
export type TableType = (typeof TableType)[keyof typeof TableType];

/**
 * CharlieTableStyles
 */

const ExpandableRowCellPadding = xcss({
	paddingInlineStart: 'space.100',
});

const RowCursorPointer = xcss({
	cursor: 'pointer',
});

const TDExtraStyle = xcss({
	borderBottom: '1px solid',
	borderInlineEnd: '1px solid',
	borderColor: 'color.border',
});
const OverflowHiddenStyle = xcss({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});
const LoadMoreWrapperStyle = xcss({ marginBlockStart: 'space.100' });
const CheckboxWrapperStyle = xcss({
	width: 'fit-content',
	marginInlineStart: 'space.negative.100',
});

const SubRowButtonWrapperStyle = xcss({
	marginInlineEnd: 'space.050',
});

const SubRowExpandableStyle = xcss({
	// @ts-expect-error - this saves us from rerendering all the cells
	position: 'var(--col-group-position)',
	left: '0',
	whiteSpace: 'nowrap',
	borderBottom: '1px solid',
	borderInlineStart: '1px solid',
	borderColor: 'color.border',
	zIndex: '1',
});

const LoadingTableStyle = xcss({ opacity: 0.7, pointerEvents: 'none' });

const TableWrapperStyle = xcss({
	display: 'flex',
	flex: 1,
	flexDirection: 'column',
	height: '100%',
	overflow: 'auto',
	borderTopLeftRadius: '7px',
	borderTopRightRadius: '7px',
});

const FooterStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	position: 'sticky',
	left: 'space.0',
});

const FooterDividerStyles = xcss({
	border: 'none',
	borderBottom: `2px solid ${token('color.border')}`,
	marginTop: 'space.250',
	width: '10%',
});

const LoadingOverlayStyle = xcss({
	position: 'absolute',
	justifyContent: 'center',
	alignItems: 'center',
	pointerEvents: 'none',
	top: 'space.0',
	left: 'space.0',
	right: 'space.0',
	bottom: 'space.0',
});

/**
 * CharlieTableProps is the props for the CharlieTable component.
 * Type T should be an Entity type that is used in the table.
 */
export interface CharlieTableProps<T extends object> {
	// tanstack duplication to be deleted

	/* onColumnOrderChange is a callback to house logic to persist re-ordering or when the user wants to show/hide columns. */
	onColumnOrderChange?:
		| TableOptions<T>['onColumnOrderChange']
		| CharlieTableHook<T>['onColumnOrderChange'];

	onGroupingChange?: TableOptions<T>['onGroupingChange'];

	/**
	 * @private
	 * @deprecated DEPRECATED in favor of tanstacks onSortingChange. This will be removed in a future version.
	 * onSortChange is a callback to house logic to persist sorting changes. */
	onSortChange?: CharlieTableHook<T>['onSortChange'];

	onSortingChange?: TableOptions<T>['onSortingChange'];
	/**
	 * @private
	 * @deprecated DEPRECATED in favor of tanstacks onExpandedChange. This will be removed in a future version.
	 *  onExpandChange is a callback to house logic to persist expanded rows.
	 */
	onExpandChange?: CharlieTableHook<T>['onExpandChange'];
	onExpandedChange?: TableOptions<T>['onExpandedChange'];
	/**
	 * @private
	 * @deprecated DEPRECATED in favor of tanstacks onRowSelectionChange. This will be removed in a future version.
	 *  onSelectionChange is a callback to house logic to persist selected rows. */
	onSelectionChange?: CharlieTableHook<T>['onSelectionChange'];
	onRowSelectionChange?: TableOptions<T>['onRowSelectionChange'];
	/**
	 * @private
	 * @deprecated DEPRECATED in favor of tanstacks onColumnSizingChange. This will be removed in a future version.
	 * onColumnWidthsChange is a callback to house logic to persist column widths.
	 */
	onColumnWidthsChange?: CharlieTableHook<T>['onColumnWidthsChange'];
	onColumnSizingChange?: TableOptions<T>['onColumnSizingChange'];
	/**
	 * onColumnPinningChange is a callback to house logic to persist column pinning.
	 */
	onColumnPinningChange?:
		| TableOptions<T>['onColumnPinningChange']
		| CharlieTableHook<T>['onColumnPinningChange'];

	onColumnVisibilityChange?: TableOptions<T>['onColumnVisibilityChange'];

	enableRowSelection?:
		| TableOptions<T>['enableRowSelection']
		| CharlieTableHook<T>['enableRowSelection'];

	/**
	 * @private
	 * @deprecated DEPRECATING in favor of tanstack columnDefs. This may be removed in a future version.
	 */
	columns?: CharlieTableHook<T>['columns'];
	columnDefs?: TableOptions<T>['columns'];

	/**
	 * @param row the row to get the id for
	 * @param index the index of the row
	 * @param parent the parent row if it exists
	 * @returns the row id
	 *
	 * This is an optional function used to get the id of a row. Defaults to the index of the row or,
	 * if nested, the indexes of the parent and child rows, separated by `.`
	 */
	getRowId?: TableOptions<T>['getRowId'];

	/**
	 * @private
	 * @deprecated DEPRECATED in favor of tanstacks defaultColumn. This will be removed in a future version.
	 */
	defaultColumnWidths?: CharlieTableHook<T>['defaultColumnWidths'];
	defaultColumn?: TableOptions<T>['defaultColumn'];

	groupedColumnMode?: TableOptions<T>['groupedColumnMode'];

	getSubRows?: TableOptions<T>['getSubRows'];

	/**
	 * renderChildComponent is a function that allows for sub-rows to be rendered.
	 * This is useful for rendering nested rows or rows that are not directly related to the parent row. (IE grouping)
	 */
	renderChildComponent?: ({
		rowData,
		tableState,
	}: {
		rowData: TanRow<T>;
		tableState: TanTableState;
	}) => ReactNode;

	/**
	 * Not all of tanstack State is fully implemented here and it may not do anything until implemented
	 */
	state?: {
		/**
		 * @private
		 * @deprecated DEPRECATED in favor of tanstacks columnSizing. This will be removed in a future version.
		 * columnWidths state is an object that contains the keys of the columns and their width.
		 *
		 * Ex. { 'columnKey': 100 }
		 */
		columnWidths?: CharlesState['columnWidths'];
		/**
		 * columnPinning state is an object that contains the keys of the columns that are pinned.
		 *
		 * Ex. { left: ['columnKey'], right: ['columnKey'] }
		 *
		 * NOTE: This is a new feature and end is not yet fully supported.
		 */
		columnPinning?: TanTableState['columnPinning'] | CharlesState['columnPinning'];
		/**
		 * sorting state is an object that contains the field and direction of the current sort.
		 * If no sort field is provided, column id is used as unique identifier.
		 *
		 * Ex. { id: 'name', desc: true }
		 */
		sorting?: CharlesState['sorting'] | TanTableState['sorting'];
	} & Partial<TanTableState>;

	/**
	 * Not all of tanstack State is fully implemented here and it may not do anything until implemented
	 */
	initialState?: {
		/**
		 * @private
		 * @deprecated DEPRECATED in favor of tanstacks columnSizing. This will be removed in a future version.
		 * columnWidths state is an object that contains the keys of the columns and their width.
		 *
		 * Ex. { 'columnKey': 100 }
		 */
		columnWidths?: CharlesState['columnWidths'];
		/**
		 * columnPinning state is an object that contains the keys of the columns that are pinned.
		 *
		 * Ex. { left: ['columnKey'], right: ['columnKey'] }
		 *
		 * NOTE: This is a new feature and end is not yet fully supported.
		 */
		columnPinning?: TanTableState['columnPinning'] | CharlesState['columnPinning'];
		/**
		 * sorting state is an object that contains the field and direction of the current sort.
		 * If no sort field is provided, column id is used as unique identifier.
		 *
		 * Ex. { id: 'name', desc: true }
		 */
		sorting?: CharlesState['sorting'] | TanTableState['sorting'];
	} & Partial<TanTableState>;

	getRowCanExpand?: TableOptions<T>['getRowCanExpand'];

	//end duplication

	/**
	 * testId for the <table> component
	 */
	testId?: string;
	/**
	 * @private
	 * @deprecated use tableState instead in order to set loading state
	 */
	isLoading?: boolean;
	/**
	 * Determine if columns can be resized via pragmatic drag + drop
	 */
	enableResizing?: boolean;
	/**
	 * Determine if columns can be reordered via pragmatic drag + drop.
	 */
	enableColumnReorder?: boolean;
	/**
	 * - Enables/disables multiple row selection for all rows in the table OR
	 * - A function that given a row, returns whether to enable/disable multiple row selection for that row's children/grandchildren
	 */
	enableMultiRowSelection?: TableOptions<T>['enableMultiRowSelection'];
	/**
	 * onLoadNext is used for infinite scrolling.
	 * This function should be called when the table triggers a "load more state". Such as when the user scrolls to the bottom of the table.
	 * @returns void
	 */
	onLoadNext?: () => void;
	/**
	 * hasNext is used for infinite scrolling. This boolean should be true if there are more rows to load.
	 */
	hasNext?: boolean;

	/**
	 * Contains the refs for certain table elements.
	 */
	refs?: {
		/**
		 * tableRef is a ref for the <Table> element
		 */
		tableRef?: RefObject<HTMLTableElement>;
		/**
		 * tableBodyRef is a ref for the <TBody> element.
		 */
		tableBodyRef?: React.RefObject<HTMLTableSectionElement>;
		/**
		 * tableContainerRef is a ref for the <Box> that surrounds the <Table>
		 */
		tableContainerRef?: React.RefObject<HTMLDivElement>;
	};

	/**
	 * tableState is used to set the current state of the table.
	 * It can take on values as defined in the TableState enum.
	 */
	tableState?: TableState;
	/**
	 * errorView is used to display an error message when tableState === TableState.error
	 */
	errorView?: ReactNode;
	/**
	 * emptyTableView is used to display a loading message when data is empty and tableState === TableState.loaded
	 */
	emptyTableView?: ReactNode;

	/**
	 * Hides the HR divider in the footer of the table that indicates end of pagination
	 */
	hidePaginationEndIndicator?: boolean;

	labels?: {
		/**
		 * The text displayed for the button that loads more data. Defaults to "Load more"
		 */
		loadMore?: string;
		/**
		 * The aria-roledescription for the sort button. Defaults to "Column sort button".
		 */
		sortButton?: string;
		/**
		 * The aria-label for the select all rows button. Defaults to "Select all rows"
		 */
		selectAllRows?: string;
		/**
		 * The aria-label for the select row button. Defaults to the "Select row"
		 */
		selectRow?: string;
		/**
		 * The aria-label for the expand row button. Defaults to "Expand row"
		 */
		expandRow?: string;
	};
	onRowClick?: (row: RowType<T>) => void;
	/**
	 * Additional information to be included in the `context` of Atlaskit analytics events that come from CharlieTable
	 */
	analyticsContext?: Record<string, any>;
	/**
	 * An optional name used to identify events for [React UFO (Unified Frontend Observability) press interactions](https://developer.atlassian.com/platform/ufo/react-ufo/react-ufo/getting-started/#quick-start--press-interactions). For more information, see [React UFO integration into Design System components](https://go.atlassian.com/react-ufo-dst-integration).
	 */
	interactionName?: string;
	rowSpacing?: RowSpacing;
	/**
	 * An optional prop that will prevent a tanstack table from resetting the expanded state when the data changes; applicable when
	 * a table with expandable rows lazy loads additional data
	 */
	autoResetExpanded?: boolean;

	renderOptions?: {
		groupingRow?: (rowData: T, state: TableOptions<T>['state']) => ReactNode;
	};

	/**
	 * @private
	 * @deprecated DEPRECATED in favor of tanstacks data. This will be removed in a future version.
	 */
	rows?: CharlieTableRow<T>[];

	data?: TableOptions<T>['data'];

	sortDescFirst?: TableOptions<T>['sortDescFirst'];

	/**
	 * Defaults to true. Enable if you prefer tanstack to sort for you
	 */
	manualSorting?: TableOptions<T>['manualSorting'];
	manualGrouping?: TableOptions<T>['manualGrouping'];
	manualExpanding?: TableOptions<T>['manualExpanding'];
	manualFiltering?: TableOptions<T>['manualFiltering'];
	manualPagination?: TableOptions<T>['manualPagination'];

	enableServerSideGrouping?: boolean;
	/**
	 * Enables/Disables the ability to remove sorting for the table.
	 * - If `true` then changing sort order will circle like: 'none' -> 'desc' -> 'asc' -> 'none' -> ...
	 * - If `false` then changing sort order will circle like: 'none' -> 'desc' -> 'asc' -> 'desc' -> 'asc' -> ...
	 * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/sorting#enablesortingremoval)
	 * @link [Guide](https://tanstack.com/table/v8/docs/guide/sorting)
	 */
	enableSortingRemoval?: TableOptions<T>['enableSortingRemoval'];

	tableType?: TableType;

	/**
	 * An optional parameter used to get custom XCSS styles for different table parts.
	 */
	xcssOverrides?: {
		/**
		 * An optional parameter used to get XCSS styles for table header (<th>) cells.
		 * Accepts either an XCSS object or a function that receives the TanStack Header and
		 * table state objects, and returns an XCSS object or undefined.
		 */
		headerCell?:
			| XCSS
			| ((header: Header<T, unknown>, tableState: TanTableState) => XCSS | undefined);

		/**
		 * An optional parameter used to get XCSS styles for table data (<td>) cells.
		 * Accepts either an XCSS object or a function that receives the TanStack Cell and
		 * table state objects, and returns an XCSS object or undefined.
		 */
		rowCell?: XCSS | ((cell: Cell<T, unknown>, tableState: TanTableState) => XCSS | undefined);
	};
	/**
	 * Allows for tables to use keyboard arrow
	 * keys (up, down, left, right) or tabbing to navigate the table.
	 *
	 * The hook also includes meta + c to copy the content. Since the
	 * table is not ADF, this only copies text content and not the
	 * full experience like images / status lozenge.
	 */
	enableKeyboardNavigation?: boolean;
}

/**
 * CharlieTable is the default table component that
 * should be used for full page table experiences. This is not meant
 * necessarily for one-off table layout content.
 *
 * Powered by Tanstack and a forked version @atlaskit/table
 * for the primitives, this table should be able to handle most and or all
 * use cases within the core experience.
 */
export const CharlieTable = <T extends object & { overrideRender?: (rowData: T) => ReactNode }>({
	labels = {
		sortButton: 'Column sort button',
		selectAllRows: 'Select all rows',
		selectRow: 'Select row',
		expandRow: 'Expand row',
		loadMore: 'Load more',
	},
	manualSorting = true,
	manualPagination = true,
	enableResizing = false,
	tableType = TableType.input,
	tableState = TableState.loaded,
	rowSpacing = 'default',
	xcssOverrides,
	enableKeyboardNavigation = false,
	...props
}: CharlieTableProps<T>) => {
	const [parentSize, setParentSize] = useState<number>(0);
	const tableRef = useRef<HTMLTableElement>(null);
	useImperativeHandle(props.refs?.tableRef, () => tableRef.current!);
	const { tableNavigationControllers } = useKeyboardNavigation();
	const infiniteScrollRef = useInfiniteScroll<HTMLTableRowElement>({
		onScrollTo: () => {
			props.onLoadNext?.();
		},
	});

	const CharlieTableDndWrapper = React.lazy(() =>
		import(
			/* webpackChunkName: '@atlaskit-internal_dnd-wrapper-charlie-table' */
			'./charlie-table-dnd-wrapper'
		).then((module) => ({ default: module.CharlieTableDndWrapper })),
	);

	const CharlieWrapper = React.useMemo(() => {
		if (props.enableColumnReorder || enableResizing) {
			return React.forwardRef<HTMLDivElement, React.ComponentProps<typeof CharlieTableDndWrapper>>(
				({ charlieTableState, tableRef, ...props }, ref) => (
					<React.Suspense fallback={<Box {...props} />}>
						<CharlieTableDndWrapper
							ref={ref}
							charlieTableState={charlieTableState}
							{...props}
							tableRef={tableRef}
						/>
					</React.Suspense>
				),
			);
		} else {
			return React.forwardRef<HTMLDivElement, React.ComponentProps<typeof CharlieTableDndWrapper>>(
				({ charlieTableState, ...props }, ref) => <Box ref={ref} {...props} />,
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.enableColumnReorder, enableResizing]);

	const dummyId = 'charlie-table-reserved-checkbox';

	const oldModifiedColumns = React.useMemo(() => {
		if (props.columnDefs) {
			return undefined;
		}
		const dummyCheckboxOldColumn: CharlieTableColumn<T> = {
			content: <Box></Box>,
			key: dummyId,
			width: 44,
			minWidth: 44,
			isSortable: false,
			cell: { render: () => <Box></Box> },
		};
		return props.enableRowSelection
			? [dummyCheckboxOldColumn, ...(props.columns ?? [])]
			: props.columns;
	}, [props.columns, props.enableRowSelection, props.columnDefs]);

	const tanModifiedColumns = React.useMemo(() => {
		if (!props.columnDefs) {
			return undefined;
		}
		const dummyCheckboxTanColumn: ColumnDef<T> = {
			header: () => <Box></Box>,
			id: dummyId,
			size: 44,
			minSize: 44,
			enableSorting: false,
			cell: () => <Box></Box>,
		};

		return props.enableRowSelection
			? [dummyCheckboxTanColumn, ...props.columnDefs]
			: props.columnDefs;
	}, [props.columnDefs, props.enableRowSelection]);

	const appendDummyCheckboxToList = React.useCallback(
		(list?: Array<string>) =>
			props.enableRowSelection && list ? [dummyId, ...(list ?? [])] : list,
		[props.enableRowSelection],
	);
	const modifiedColumnOrder = React.useMemo(
		() => appendDummyCheckboxToList(props.state?.columnOrder),
		[appendDummyCheckboxToList, props.state?.columnOrder],
	);
	const modifiedInitialColumnOrder = React.useMemo(
		() => appendDummyCheckboxToList(props.initialState?.columnOrder),
		[appendDummyCheckboxToList, props.initialState?.columnOrder],
	);

	const modifiedColumnPinningStart = React.useMemo(
		() =>
			appendDummyCheckboxToList(
				// @ts-expect-error ignoring anything with old charlietable
				props.state?.columnPinning?.start ?? props.state?.columnPinning?.left,
			),
		[
			appendDummyCheckboxToList,
			// @ts-expect-error ignoring anything with old charlietable
			props.state?.columnPinning?.start,
			props.state?.columnPinning?.left,
		],
	);
	const modifiedInitialColumnPinningStart = React.useMemo(
		() =>
			appendDummyCheckboxToList(
				// @ts-expect-error ignoring anything with old charlietable
				props.initialState?.columnPinning?.start ?? props.initialState?.columnPinning?.left,
			),

		[
			appendDummyCheckboxToList,
			// @ts-expect-error ignoring anything with old charlietable
			props.initialState?.columnPinning?.start,
			props.initialState?.columnPinning?.left,
		],
	);
	const modifiedColumnPinning = React.useMemo(
		() =>
			modifiedColumnPinningStart
				? { ...props.state?.columnPinning, start: modifiedColumnPinningStart }
				: props.state?.columnPinning,
		[modifiedColumnPinningStart, props.state?.columnPinning],
	);

	const modifiedInitialColumnPinning = React.useMemo(
		() =>
			modifiedInitialColumnPinningStart
				? { ...props.initialState?.columnPinning, start: modifiedInitialColumnPinningStart }
				: props.initialState?.columnPinning,
		[modifiedInitialColumnPinningStart, props.initialState?.columnPinning],
	);

	const convertedCharles = useConvertCharlieTableToTanStack({
		...props,
		columnDefs: tanModifiedColumns,
		columns: oldModifiedColumns,
		manualPagination,
		manualSorting,
		state: {
			...props.state,
			columnPinning: modifiedColumnPinning,
			columnOrder: modifiedColumnOrder,
		},
		initialState: {
			...props.initialState,
			columnPinning: modifiedInitialColumnPinning,
			columnOrder: modifiedInitialColumnOrder,
		},
	});

	const columns = React.useMemo(() => convertedCharles.columns, [convertedCharles.columns]);
	const data = React.useMemo(() => convertedCharles.data, [convertedCharles.data]);

	const table = useReactTable<T>({
		...convertedCharles,
		columns,
		data,
		autoResetExpanded: props.autoResetExpanded,
	});

	const hasWidths =
		[...Object.keys(table.getState().columnSizing ?? {})].length > 0 || enableResizing;

	const totalTableWidth = table.getTotalSize();
	const columnCssVars = React.useMemo(() => {
		const cols = table.getAllLeafColumns();
		const colCss: Record<string, string | undefined> = {};
		cols.forEach((col) => {
			const isPinned = col.getIsPinned();
			if (!isPinned || !col) {
				return;
			}

			const colKeyPrefix = `--col-${col.id}`;

			colCss[`${colKeyPrefix}-left`] =
				isPinned === 'left' ? `${col.getStart('left')}px` : undefined;
			colCss[`${colKeyPrefix}-right`] =
				isPinned === 'right' ? `${col.getAfter('right')}px` : undefined;
			// @ts-expect-error-next-line - none of the zIndex ADS tokens equate to 1
			colCss[`${colKeyPrefix}-zIndex`] = 1;
			colCss[`${colKeyPrefix}-position`] = 'sticky';
		});
		if (table?.getState()?.columnPinning?.left && table.getState().columnPinning.left!.length > 0) {
			colCss[`--col-group-position`] = 'sticky';
		}
		return colCss;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [table.options.state]);

	const isEmptyState = table.options.data.length === 0 && tableState === TableState.loaded;

	return (
		//normally you shouldn't do this, but was having trouble passing the generic with the lazy loaded component
		//@ts-expect-error lazy loading isses
		<CharlieWrapper
			xcss={TableWrapperStyle}
			ref={props.refs?.tableContainerRef}
			{...((props.enableColumnReorder || enableResizing) && {
				charlieTableState: table,
				tableRef: tableRef,
			})}
		>
			<WidthObserver setWidth={setParentSize} />
			<Table
				testId={props.testId}
				ref={tableRef}
				xcss={[
					props.isLoading || tableState === TableState.loading ? LoadingTableStyle : false,
					xcss({
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- this is needed to prevent rerenders
						...columnCssVars,
						borderCollapse: 'separate',
						borderSpacing: '0px',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
						tableLayout: hasWidths ? 'fixed' : 'auto',
						width: '100%',
						overflow: 'auto',
						margin: 'space.0',
					}),
				]}
			>
				<Colgroup>
					{table.getHeaderGroups().map((headerGroup) => {
						return headerGroup.headers.map((header) => {
							if (header?.column == null) {
								return <Fragment></Fragment>;
							}

							return (
								<Col
									key={header.id}
									xcss={xcss({
										width:
											// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
											(!header.column.getIsLastColumn() ||
												(parentSize && parentSize < totalTableWidth)) &&
											enableResizing
												? `${header.column.getSize()}px`
												: undefined,
										overflow: 'hidden',
										textOverflow: 'ellipsis',
									})}
								/>
							);
						});
					})}
				</Colgroup>
				<THead>
					{table.getHeaderGroups().map((headerGroup) => {
						return (
							<TR key={headerGroup.id}>
								{headerGroup.headers.map((header, idx) => (
									<CharlieTableHeader<T>
										key={header.id}
										labels={labels}
										charlieTableColumn={header}
										index={idx}
										enableResizing={enableResizing && header.column.getCanResize()}
										charlieTableState={table}
										tableType={tableType}
										enableColumnReorder={props.enableColumnReorder}
										enableRowSelection={props.enableRowSelection}
										hasWidths={hasWidths}
										xcss={
											typeof xcssOverrides?.headerCell === 'function'
												? xcssOverrides.headerCell(header, table.getState())
												: undefined
										}
									/>
								))}
							</TR>
						);
					})}
				</THead>
				{React.useMemo(
					() => (
						<TBody ref={props.refs?.tableBodyRef}>
							{table.getRowModel().rows ? (
								table.getRowModel().rows.map((row, index) => {
									return (
										<Fragment key={row.id}>
											{row.original?.overrideRender ||
											(row.getIsGrouped() && props.renderOptions?.groupingRow) ? (
												<Fragment>
													<Row
														spacing={rowSpacing}
														onClick={(e: React.MouseEvent<HTMLElement>) => {
															// @ts-expect-error Prevent row click if a link was clicked
															if (e.target.closest('a')) {
																return;
															}
															props.onRowClick?.(row);
														}}
														xcss={props.onRowClick && RowCursorPointer}
													>
														<TD
															xcss={[
																SubRowExpandableStyle,
																typeof xcssOverrides?.rowCell === 'function'
																	? xcssOverrides.rowCell(
																			row.getVisibleCells()[0],
																			table.getState(),
																		)
																	: undefined,
															]}
														>
															<Flex alignItems="center">
																{(props.renderChildComponent || props.renderOptions?.groupingRow) &&
																	row.getCanExpand() && (
																		<Box
																			xcss={[
																				SubRowButtonWrapperStyle,
																				xcss({
																					// @ts-expect-error - needed for nested styles
																					// eslint-disable-next-line @compiled/no-suppress-xcss
																					paddingInlineStart:
																						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
																						row.getCanExpand()
																							? `${(row.depth + 1) * 8}px`
																							: `${32 + (row.depth + 1) * 8}px`,
																				}),
																			]}
																		>
																			<IconButton
																				spacing="compact"
																				testId="expand-row-button"
																				appearance="subtle"
																				interactionName={props.interactionName}
																				analyticsContext={{
																					componentName: 'charlieTableExpandRowButton',
																					...props.analyticsContext,
																				}}
																				aria-pressed={row.getIsExpanded()}
																				label={labels.expandRow}
																				icon={(iconProps) => (
																					<ExpandIcon
																						isExpanded={row.getIsExpanded() ?? false}
																						{...iconProps}
																					/>
																				)}
																				onClick={(e) => {
																					e.stopPropagation();
																					row.toggleExpanded();
																				}}
																			/>
																		</Box>
																	)}
																{row.original.overrideRender &&
																	row.original.overrideRender(row.original)}
																{props.renderOptions?.groupingRow &&
																	props.renderOptions.groupingRow(row.original, table.getState())}
															</Flex>
														</TD>
														{row.getVisibleCells().length > 1 && (
															<TD
																colSpan={row.getVisibleCells().length - 1}
																xcss={[
																	TDExtraStyle,
																	typeof xcssOverrides?.rowCell === 'function'
																		? xcssOverrides.rowCell(
																				row.getVisibleCells()[1],
																				table.getState(),
																			)
																		: undefined,
																]}
															/>
														)}
													</Row>
												</Fragment>
											) : (
												<Row
													key={row.id}
													spacing={rowSpacing}
													onClick={(e: React.MouseEvent<HTMLElement>) => {
														// @ts-expect-error Prevent row click if a link was clicked
														if (e.target.closest('a')) {
															return;
														}
														props.onRowClick?.(row);
													}}
													xcss={props.onRowClick && RowCursorPointer}
												>
													{row.getVisibleCells().map((cell, index) => (
														<RowCell
															tableType={tableType}
															{...(enableKeyboardNavigation && {
																tabIndex: 0,
																onKeyDown: tableNavigationControllers,
															})}
															xcss={[
																OverflowHiddenStyle,
																cell.row.getCanExpand() &&
																	((props.enableRowSelection && index === 1) ||
																		(!props.enableRowSelection && index === 0)) &&
																	ExpandableRowCellPadding,
																cell.column.getIsPinned() &&
																	xcss({
																		// @ts-expect-error - this saves us from rerendering all the cells
																		// eslint-disable-next-line @compiled/no-suppress-xcss, @atlaskit/ui-styling-standard/no-unsafe-values
																		zIndex: `var(--col-${cell.column.id}-zIndex)`,
																		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
																		left: `var(--col-${cell.column.id}-left)`,
																		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
																		right: `var(--col-${cell.column.id}-right)`,
																		// @ts-expect-error - this saves us from rerendering all the cells
																		// eslint-disable-next-line @compiled/no-suppress-xcss, @atlaskit/ui-styling-standard/no-unsafe-values
																		position: `var(--col-${cell.column.id}-position)`,
																	}),
																typeof xcssOverrides?.rowCell === 'function'
																	? xcssOverrides.rowCell(cell, table.getState())
																	: undefined,
															]}
															key={cell.id}
															// colSpan={column?.cell?.colSpan ?? undefined}
														>
															{index === 0 && props.enableRowSelection ? (
																<Box xcss={CheckboxWrapperStyle}>
																	<Checkbox
																		analyticsContext={{
																			componentName: 'charlieTableExpandRowButton',
																			...props.analyticsContext,
																		}}
																		aria-label={labels.selectRow}
																		isDisabled={!row.getCanSelect()}
																		isChecked={row.getIsSelected()}
																		onClick={(e) => {
																			e.stopPropagation();
																			row.toggleSelected();
																		}}
																	/>
																</Box>
															) : (
																<Flex
																	alignItems="center"
																	xcss={[
																		OverflowHiddenStyle,
																		((props.enableRowSelection && index === 1) ||
																			(!props.enableRowSelection && index === 0)) &&
																			!row.getParentRow()?.getIsGrouped() &&
																			xcss({
																				//@ts-expect-error needed for logic
																				// eslint-disable-next-line @compiled/no-suppress-xcss
																				paddingInlineStart:
																					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
																					`${row.depth * 8}px`,
																			}),
																	]}
																>
																	{row.getCanExpand() &&
																		((props.enableRowSelection && index === 1) ||
																			(!props.enableRowSelection && index === 0)) && (
																			<Box xcss={SubRowButtonWrapperStyle}>
																				<IconButton
																					spacing="compact"
																					testId="expand-row-button"
																					interactionName={props.interactionName}
																					analyticsContext={{
																						componentName: 'charlieTableExpandRowButton',
																						...props.analyticsContext,
																					}}
																					appearance="subtle"
																					aria-pressed={row.getIsExpanded()}
																					label={labels.expandRow}
																					icon={(iconProps) => (
																						<ExpandIcon
																							isExpanded={row.getIsExpanded() ?? false}
																							{...iconProps}
																						/>
																					)}
																					onClick={(e) => {
																						e.stopPropagation();
																						row.toggleExpanded();
																					}}
																				/>
																			</Box>
																		)}
																	{flexRender(cell.column.columnDef.cell, cell.getContext())}
																</Flex>
															)}
														</RowCell>
													))}
												</Row>
											)}
											{row.getIsExpanded() && props.renderChildComponent && (
												<Fragment>
													{props?.renderChildComponent({
														rowData: row,
														tableState: table.getState(),
													})}
												</Fragment>
											)}
											{props.hasNext &&
												//we pagination with child components is not supported yet
												!props.renderChildComponent &&
												index === Math.floor((table.getRowModel().rows?.length ?? 0) * 0.92) && (
													// if user is 92% through the table, load more rows
													<tr key={'loadMoreTrigger'} ref={infiniteScrollRef} />
												)}
										</Fragment>
									);
								})
							) : (
								<Fragment></Fragment>
							)}
						</TBody>
					),
					// eslint-disable-next-line react-hooks/exhaustive-deps
					[
						rowSpacing,
						table.options.state.rowSelection,
						table.options.state.sorting,
						table.options.state.expanded,
						table.options.state.columnOrder,
						table.options.state.grouping,
						table.options.state.columnVisibility,
						table.options.data,
					],
				)}
			</Table>
			{((props.hasNext != null && props.onLoadNext != null) || props.isLoading != null) && (
				<Inline xcss={FooterStyles}>
					{props.hasNext && props.onLoadNext ? (
						<Box xcss={LoadMoreWrapperStyle}>
							<Button
								testId="prev-page-test-id"
								isLoading={props.isLoading || tableState === TableState.loading}
								onClick={() => {
									props.onLoadNext?.();
								}}
								spacing="compact"
							>
								{labels.loadMore}
							</Button>
						</Box>
					) : (
						<Fragment>
							{!(
								isEmptyState ||
								tableState === TableState.error ||
								props.hidePaginationEndIndicator
							) && (
								<Box
									as="hr"
									testId="table-footer-divider"
									xcss={FooterDividerStyles}
									role="presentation"
								/>
							)}
						</Fragment>
					)}
				</Inline>
			)}

			{(props.isLoading || tableState === TableState.loading) && (
				<Flex xcss={LoadingOverlayStyle}>
					<Spinner size="large" testId="common-table-spinner" />
				</Flex>
			)}
			{tableState === TableState.error && props.errorView}
			{isEmptyState && props.emptyTableView}
		</CharlieWrapper>
	);
};
